import React from 'react';

import { FormattedMessage, useIntl } from 'react-intl';

import { Modal, Form, Button, notification } from 'antd';

import AreaReportForm from '../AreaReportForm';
import { ModalProps } from '@/types/ModalProps';
import { ReportAreasFormValues } from '@/core/domain/reports/operational/Report.types';
import { useGenerateAreasReport } from '@/core/domain/reports/operational/Report.hooks';
import { useDefaultFormErrorNotification, useReportGenerate } from '@/hooks';
import useFileLoading, { FileType } from '@/hooks/useFileLoading';
import { ReportCallback } from '@/hooks/useReportGenerate';
import SpaceForButton from '@/components/_common/SpaceForButtons';

function AreaReportModal({ visible, handleVisible }: ModalProps) {
  const intl = useIntl();
  const mutation = useGenerateAreasReport();
  const [form] = Form.useForm<ReportAreasFormValues>();
  const { sendFormErrorNotification } = useDefaultFormErrorNotification();
  const { fileLoading, setFileLoading, setFileUnloading, FILE_FORMAT } =
    useFileLoading();
  const report = useReportGenerate();

  const handleGenerateReport = async (
    format: FileType,
    callback?: ReportCallback,
  ) => {
    setFileLoading(format);
    try {
      const values = await form.validateFields();

      const obj = {
        ...values,
        active: values.active ? JSON.parse(values.active) : undefined,
        subareaSituation: values.subareaSituation
          ? JSON.parse(values.subareaSituation)
          : undefined,
        format: FILE_FORMAT[format],
      };

      const result = await mutation.mutateAsync(obj);

      if (callback) {
        callback(result);
      }

      notification.success({
        message: intl.formatMessage({
          id: 'areas.notification.generate.report.title',
        }),
        description: intl.formatMessage({
          id: 'areas.notification.generate.report.description',
        }),
      });
      form.resetFields();
      handleVisible();
    } catch (err) {
      sendFormErrorNotification(err);
    }
    setFileUnloading(format);
  };

  return (
    <Modal
      width={660}
      visible={visible}
      onCancel={handleVisible}
      title={intl.formatMessage({ id: 'areas.modal.report.title' })}
      footer={
        <SpaceForButton justifyContent="end">
          <Button
            onClick={() =>
              handleGenerateReport('REPORT_VIEW', (result) =>
                report.openFile({
                  result,
                  fileName: 'areas.pdf',
                  fileType: 'application/pdf',
                }),
              )
            }
            type="primary"
            key="viewReport"
            loading={fileLoading.REPORT_VIEW}
            block
          >
            <FormattedMessage id="areas.modal.report.buttons.viewReport" />
          </Button>
          <Button
            type="primary"
            key="PDF"
            onClick={() =>
              handleGenerateReport('PDF', (result) =>
                report.downloadFile({ result, fileName: 'areas.pdf' }),
              )
            }
            loading={fileLoading.PDF}
            block
          >
            <FormattedMessage id="areas.modal.report.buttons.pdf" />
          </Button>
          <Button
            type="primary"
            key="CSV"
            onClick={() =>
              handleGenerateReport('CSV', (result) =>
                report.downloadFile({ result, fileName: 'areas.csv' }),
              )
            }
            loading={fileLoading.CSV}
            block
          >
            <FormattedMessage id="areas.modal.report.buttons.csv" />
          </Button>
        </SpaceForButton>
      }
    >
      <AreaReportForm form={form} />
    </Modal>
  );
}

export default AreaReportModal;
